/**
 * 🎸 Motor de Canciones — Highway View para Bordona
 *
 * Utilidades adicionales sobre guitarEngine para visualización
 * tipo Rocksmith: asignación óptima de posiciones + rendering.
 */

import { getNoteAtPosition, type MusicXmlNote, type NoteName } from "./guitarEngine";

// ============================================
// Tipos y utilidades de compás
// ============================================

export interface TimedMusicXmlNote extends MusicXmlNote {
  startTime: number;
  voice?: number;
  degree?: number;
  leftHandFingering?: string;
  rightHandPluck?: string;
  tieStart?: boolean;
  tieStop?: boolean;
  /** Tablatura MusicXML (<technical><string>/<fret>) */
  tabString?: number;
  tabFret?: number;
}

export interface MeasureInfo {
  /** startTime global del compás (en <divisions>). */
  startTime: number;
  /** Longitud completa del compás según el compás (en <divisions>). */
  length: number;
  /** Notas reales escritas dentro del compás (max entre voces). */
  contentLength: number;
  /** True si contentLength < length (anacrusa / compás incompleto). */
  isPickup: boolean;
  /** Compás en el que vive: {beats, beatType}. */
  timeSignature: { beats: number; beatType: number };
  /** <motion divisions> activo dentro del compás. */
  divisions: number;
}

export interface ParsedSong {
  notes: TimedMusicXmlNote[];
  divisions: number;
  tempo: number;
  timeSignature: { beats: number; beatType: number };
  totalDivisions: number;
  /** Siempre -1 en Bordona: clave de sol 8vb en pantalla. */
  clefOctaveChange: number;
  /** Valor leído del MusicXML (0 = clave de sol normal, -1 = 8vb explícito). */
  xmlClefOctaveChange: number;
  /** Metadatos por compás (anacrusa, longitud real, etc.). */
  measures: MeasureInfo[];
}

/** Clave de sol 8vb para visualización de guitarra. */
export const GUITAR_DISPLAY_CLEF_OCTAVE_CHANGE = -1;

/** Una octava entre escritura en clave de sol normal y sonido real de guitarra. */
export const GUITAR_SOUND_OCTAVE_SHIFT = 12;

/**
 * MIDI de sonido real para mástil / micrófono.
 * - XML con 8vb (MuseScore): el pitch del XML ya es el sonido.
 * - XML sin 8vb: restar una octava (tercera línea = Si3, no Si4).
 */
export function getGuitarSoundMidi(rawMidi: number, xmlClefOctaveChange: number): number {
  if (xmlClefOctaveChange === -1) return rawMidi;
  return rawMidi - GUITAR_SOUND_OCTAVE_SHIFT;
}

/**
 * Octava Vex (`c/5` → 5) a partir del MIDI de **sonido** (mismo que el mástil).
 * En guitarra con clave 8vb la escritura va una octava por encima del nombre científico.
 */
export function staffVexOctaveFromMidi(midi: number, guitar8vbWritten = true): number {
  const scientific = Math.floor(midi / 12) - 1;
  return guitar8vbWritten ? scientific + 1 : scientific;
}

/** Clave VexFlow `step/octave` alineada al sonido real (independiente del `<octave>` del XML). */
export function staffVexKeyFromNote(
  note: { step: string; midi: number },
  guitar8vbWritten = true
): string {
  const octave = staffVexOctaveFromMidi(note.midi, guitar8vbWritten);
  return `${note.step.toLowerCase()}/${octave}`;
}

/**
 * @deprecated VexFlow no desplaza notas con el glifo 8vb; usar `staffVexKeyFromNote`.
 * Se mantiene en 0 para no desalinear partituras ya corregidas en el XML.
 */
export function getVexOctaveAdjust(_xmlClefOctaveChange: number): number {
  return 0;
}

interface MeasureTimingContext {
  divisions: number;
  beats: number;
  beatType: number;
}

/** Duración de un compás en unidades <divisions> (negra = `motion divisions`). */
export function getMeasureLengthDivisions(
  beats: number,
  beatType: number,
  divisions: number
): number {
  return Math.round(beats * (4 / beatType) * divisions);
}

/**
 * Mapea tiempo MusicXML a posición visual. Con anacrusa compacta (sin relleno de compás),
 * el contenido conserva su instante real en el XML.
 */
export function fretboardVisualDivision(globalDiv: number, measureInfos: MeasureInfo[]): number {
  return globalDiv;
}

function parseAttributesBlock(attrsXml: string, ctx: MeasureTimingContext): MeasureTimingContext {
  const divMatch = attrsXml.match(/<divisions>(\d+)<\/divisions>/);
  const beatsMatch = attrsXml.match(/<beats>(\d+)<\/beats>/);
  const beatTypeMatch = attrsXml.match(/<beat-type>(\d+)<\/beat-type>/);
  return {
    divisions: divMatch ? parseInt(divMatch[1], 10) : ctx.divisions,
    beats: beatsMatch ? parseInt(beatsMatch[1], 10) : ctx.beats,
    beatType: beatTypeMatch ? parseInt(beatTypeMatch[1], 10) : ctx.beatType,
  };
}

function midiStringPreferenceKey(voice: number, midi: number): string {
  return `${voice}:${midi}`;
}

function positionFromTablature(
  note: TimedMusicXmlNote
): { string: number; fret: number; note: NoteName; midi: number } | null {
  if (note.tabString === undefined || note.tabFret === undefined) return null;
  const s = note.tabString;
  const f = note.tabFret;
  if (s < 1 || s > 6 || f < 0) return null;
  const pos = getNoteAtPosition(s, f);
  if (pos.midi === note.midi) {
    return { string: s, fret: f, note: note.step as NoteName, midi: note.midi };
  }
  return { string: s, fret: f, note: note.step as NoteName, midi: note.midi };
}

// ============================================
// Parser MusicXML completo (con metadatos y tiempos)
// ============================================

export function parseMusicXmlFull(
  xmlString: string,
  opts?: ParseMusicXmlOptions
): ParsedSong {
  return parseMusicXmlFullWithOptions(xmlString, opts);
}

export type SongParseSoundMode = 'guitar' | 'concert';

export interface ParseMusicXmlOptions {
  /** `guitar` (default Bordona) aplica 8vb/sonido de cuerda; `concert` deja el pitch del XML. */
  soundMode?: SongParseSoundMode;
}

export function parseMusicXmlFullWithOptions(
  xmlString: string,
  opts: ParseMusicXmlOptions = {}
): ParsedSong {
  const soundMode = opts.soundMode ?? 'guitar';
  const notes: TimedMusicXmlNote[] = [];

  const globalDivMatch = xmlString.match(/<divisions>(\d+)<\/divisions>/);
  let ctx: MeasureTimingContext = {
    divisions: globalDivMatch ? parseInt(globalDivMatch[1], 10) : 1,
    beats: 4,
    beatType: 4,
  };

  const globalBeatsMatch = xmlString.match(/<beats>(\d+)<\/beats>/);
  const globalBeatTypeMatch = xmlString.match(/<beat-type>(\d+)<\/beat-type>/);
  if (globalBeatsMatch) ctx.beats = parseInt(globalBeatsMatch[1], 10);
  if (globalBeatTypeMatch) ctx.beatType = parseInt(globalBeatTypeMatch[1], 10);

  const soundTempoMatch = xmlString.match(/<sound[^>]*tempo="([\d.]+)"/);
  const perMinuteMatch = xmlString.match(/<per-minute>(\d+)<\/per-minute>/);
  const tempo = soundTempoMatch
    ? Math.round(parseFloat(soundTempoMatch[1]))
    : perMinuteMatch
      ? parseInt(perMinuteMatch[1], 10)
      : 120;

  const clefOctaveChangeMatch = xmlString.match(/<clef-octave-change>(-?\d+)<\/clef-octave-change>/);
  const xmlClefOctaveChange = clefOctaveChangeMatch ? parseInt(clefOctaveChangeMatch[1], 10) : 0;

  const transposeMatch = xmlString.match(/<transpose>[\s\S]*?<\/transpose>/);
  const transposeOctaveMatch = transposeMatch
    ? transposeMatch[0].match(/<octave-change>(-?\d+)<\/octave-change>/)
    : null;
  const transposeOctaveChange = transposeOctaveMatch ? parseInt(transposeOctaveMatch[1], 10) : 0;

  const keyMatch = xmlString.match(/<key>[\s\S]*?<\/key>/);
  const fifthsMatch = keyMatch ? keyMatch[0].match(/<fifths>(-?\d+)<\/fifths>/) : null;
  const modeMatch = keyMatch ? keyMatch[0].match(/<mode>(\w+)<\/mode>/) : null;
  const fifths = fifthsMatch ? parseInt(fifthsMatch[1], 10) : 0;
  const mode = modeMatch ? modeMatch[1] : "major";
  const majorRoot = ((fifths * 7) % 12 + 12) % 12;
  const tonic = mode === "minor" ? (majorRoot - 3 + 12) % 12 : majorRoot;

  function semitoneToDegree(offset: number): number {
    const map: Record<number, number> = {
      0: 1, 1: 1,
      2: 2,
      3: 3, 4: 3,
      5: 4, 6: 4,
      7: 5,
      8: 6, 9: 6,
      10: 7, 11: 7,
    };
    return map[offset] ?? 1;
  }

  const measureRegex = /<measure\s+[^>]*>[\s\S]*?<\/measure>/g;
  const measures = xmlString.match(measureRegex) || [];

  let measureGlobalStart = 0;
  const measureInfos: MeasureInfo[] = [];

  const rawNotes: {
    step: string;
    alter?: number;
    octave: number;
    duration: number;
    type: string;
    rawMidi: number;
    startTime: number;
    hasDot: boolean;
    isRest: boolean;
    voice?: number;
    leftHandFingering?: string;
    rightHandPluck?: string;
    tieStart: boolean;
    tieStop: boolean;
    tabString?: number;
    tabFret?: number;
  }[] = [];

  for (const measureXml of measures) {
    const attrsMatch = measureXml.match(/<attributes>[\s\S]*?<\/attributes>/);
    if (attrsMatch) {
      ctx = parseAttributesBlock(attrsMatch[0], ctx);
    }

    const measureLength = getMeasureLengthDivisions(ctx.beats, ctx.beatType, ctx.divisions);

    const elementRegex = /<(note|backup|forward)[\s>][\s\S]*?<\/\1>/g;
    const elements = measureXml.match(elementRegex) || [];
    let currentTime = 0;
    let lastAttackTime = 0;
    let measureContentEnd = 0;
    const measureStartGlobal = measureGlobalStart;

    for (const el of elements) {
      const tag = el.match(/<(note|backup|forward)/)?.[1];

      if (tag === "backup") {
        const durMatch = el.match(/<duration>(\d+)<\/duration>/);
        if (durMatch) currentTime -= parseInt(durMatch[1], 10);
        if (currentTime < 0) currentTime = 0;
        continue;
      }

      if (tag === "forward") {
        const durMatch = el.match(/<duration>(\d+)<\/duration>/);
        if (durMatch) currentTime += parseInt(durMatch[1], 10);
        measureContentEnd = Math.max(measureContentEnd, currentTime);
        continue;
      }

      const noteXml = el;

      if (noteXml.includes("<grace") || noteXml.includes("<cue")) {
        continue;
      }

      const isRest = noteXml.includes("<rest") || noteXml.includes("<rest/");
      const isChord = noteXml.includes("<chord") || noteXml.includes("<chord/");
      const durationMatch = noteXml.match(/<duration>(\d+)<\/duration>/);
      const duration = durationMatch ? parseInt(durationMatch[1], 10) : ctx.divisions;

      const typeMatch = noteXml.match(/<type>([\w-]+)<\/type>/);
      const noteType = typeMatch ? typeMatch[1] : "quarter";
      const hasDot = noteXml.includes("<dot") || noteXml.includes("<dot/>");
      const effectiveDuration = duration;

      const fingeringMatch = noteXml.match(/<fingering>(\d)<\/fingering>/);
      const pluckMatch = noteXml.match(/<pluck>([pimac])<\/pluck>/);
      const leftHandFingering = fingeringMatch ? fingeringMatch[1] : undefined;
      const rightHandPluck = pluckMatch ? pluckMatch[1] : undefined;

      const stringMatch = noteXml.match(/<string>(\d)<\/string>/);
      const fretMatch = noteXml.match(/<fret>(\d+)<\/fret>/);
      const tabString = stringMatch ? parseInt(stringMatch[1], 10) : undefined;
      const tabFret = fretMatch ? parseInt(fretMatch[1], 10) : undefined;

      const tieStart =
        noteXml.includes('<tie type="start"') || noteXml.includes('<tied type="start"');
      const tieStop =
        noteXml.includes('<tie type="stop"') || noteXml.includes('<tied type="stop"');

      const noteStart = measureGlobalStart + (isChord ? lastAttackTime : currentTime);

      if (isRest) {
        rawNotes.push({
          step: "rest",
          octave: 0,
          duration: effectiveDuration,
          type: noteType,
          rawMidi: 0,
          startTime: noteStart,
          hasDot,
          isRest: true,
          leftHandFingering: undefined,
          rightHandPluck: undefined,
          tieStart: false,
          tieStop: false,
        });
        if (!isChord) {
          lastAttackTime = currentTime;
          currentTime += effectiveDuration;
          measureContentEnd = Math.max(measureContentEnd, currentTime);
        }
        continue;
      }

      const stepMatch = noteXml.match(/<step>([A-G])<\/step>/);
      const octaveMatch = noteXml.match(/<octave>(\d)<\/octave>/);
      const alterMatch = noteXml.match(/<alter>(-?\d)<\/alter>/);

      if (stepMatch && octaveMatch) {
        const step = stepMatch[1];
        const octave = parseInt(octaveMatch[1], 10);
        const alter = alterMatch ? parseInt(alterMatch[1], 10) : 0;
        const baseSemitone: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
        const semitone = baseSemitone[step] + alter;
        const rawMidi = (octave + 1) * 12 + semitone;

        const voiceMatch = noteXml.match(/<voice>(\d+)<\/voice>/);
        const voice = voiceMatch ? parseInt(voiceMatch[1], 10) : 1;

        rawNotes.push({
          step,
          alter: alter !== 0 ? alter : undefined,
          octave,
          duration: effectiveDuration,
          type: noteType,
          rawMidi,
          startTime: noteStart,
          hasDot,
          isRest: false,
          voice,
          leftHandFingering,
          rightHandPluck,
          tieStart,
          tieStop,
          tabString,
          tabFret,
        });

        if (!isChord) {
          lastAttackTime = currentTime;
          currentTime += effectiveDuration;
          measureContentEnd = Math.max(measureContentEnd, currentTime);
        }
      }
    }

    const rawContentLength = Math.max(measureContentEnd, 0);
    /**
     * Anacrusa real: el XML acorta el compás frente al compás nominal (`raw < length`).
     * Falso positivo muy frecuente: exportadores omiten silencios finales; entonces
     * `rawContentLength` queda solo un pulso (o pocos) por debajo de `measureLength`.
     * Si el hueco final es ≤ ~1 pulso del compás, tratamos el compás como completo.
     */
    const oneBeatDiv =
      ctx.beatType === 8 ? Math.max(1e-9, ctx.divisions / 2) : Math.max(1e-9, ctx.divisions);
    const trailingGap = measureLength - rawContentLength;
    const shortBar = rawContentLength > 0 && rawContentLength < measureLength - 0.001;
    const looksLikeOmittedTrailingRest =
      shortBar && trailingGap > 0.001 && trailingGap <= oneBeatDiv * 1.08 + 0.001;
    const contentLength = looksLikeOmittedTrailingRest ? measureLength : rawContentLength;
    measureInfos.push({
      startTime: measureStartGlobal,
      length: measureLength,
      contentLength,
      isPickup: shortBar && !looksLikeOmittedTrailingRest,
      timeSignature: { beats: ctx.beats, beatType: ctx.beatType },
      divisions: ctx.divisions,
    });
    measureGlobalStart += measureLength;
  }

  const octaveOffset = transposeOctaveChange !== 0 ? transposeOctaveChange * 12 : 0;

  for (const n of rawNotes) {
    const concertMidi = n.isRest ? 0 : n.rawMidi + octaveOffset;
    const midi =
      n.isRest
        ? 0
        : soundMode === 'concert'
          ? concertMidi
          : getGuitarSoundMidi(concertMidi, xmlClefOctaveChange);
    const degree = n.isRest ? undefined : semitoneToDegree(((midi % 12 - tonic + 12) % 12));
    notes.push({
      step: n.step,
      alter: n.alter,
      octave: n.octave,
      duration: n.duration,
      type: n.type,
      midi,
      startTime: n.startTime,
      hasDot: n.hasDot,
      isRest: n.isRest,
      voice: n.voice,
      degree,
      leftHandFingering: n.leftHandFingering,
      rightHandPluck: n.rightHandPluck,
      tieStart: n.tieStart,
      tieStop: n.tieStop,
      tabString: n.tabString,
      tabFret: n.tabFret,
    } as TimedMusicXmlNote);
  }

  notes.sort(
    (a, b) =>
      a.startTime - b.startTime ||
      (a.voice ?? 1) - (b.voice ?? 1) ||
      a.midi - b.midi
  );

  const uniqueNotes: TimedMusicXmlNote[] = [];
  const seen = new Set<string>();
  for (const n of notes) {
    const key = `${n.startTime}|${n.voice ?? 1}|${n.midi}|${n.duration}|${n.type}|${n.hasDot ? 1 : 0}|${n.isRest ? 1 : 0}`;
    if (!seen.has(key)) {
      seen.add(key);
      uniqueNotes.push(n);
    }
  }

  return {
    notes: uniqueNotes,
    divisions: ctx.divisions,
    tempo,
    timeSignature: { beats: ctx.beats, beatType: ctx.beatType },
    totalDivisions: measureGlobalStart,
    clefOctaveChange: GUITAR_DISPLAY_CLEF_OCTAVE_CHANGE,
    xmlClefOctaveChange,
    measures: measureInfos,
  };
}

// ============================================
// Colores por cuerda (estilo Rocksmith)
// ============================================

export const STRING_COLORS: Record<number, string> = {
  1: "#4FC3F7",
  2: "#AB47BC",
  3: "#F44336",
  4: "#FFCA28",
  5: "#66BB6A",
  6: "#FF9800",
};

export const STRING_COLORS_DARK: Record<number, string> = {
  1: "#01579B",
  2: "#4A148C",
  3: "#B71C1C",
  4: "#F57F17",
  5: "#1B5E20",
  6: "#E65100",
};

// ============================================
// Posicionador óptimo en mástil
// ============================================

export interface PositionedNote extends TimedMusicXmlNote {
  string: number;
  fret: number;
  x: number;
  width: number;
  color: string;
  colorDark: string;
  fretLabel: string;
  typeLabel: string;
}

function getAllPositions(
  targetMidi: number,
  maxFret = 15
): { string: number; fret: number; note: NoteName; midi: number }[] {
  const all: { string: number; fret: number; note: NoteName; midi: number }[] = [];
  for (let s = 1; s <= 6; s++) {
    for (let f = 0; f <= maxFret; f++) {
      const pos = getNoteAtPosition(s, f);
      if (pos.midi === targetMidi) {
        all.push(pos);
      }
    }
  }
  return all;
}

export function findBestPosition(
  targetMidi: number,
  prevPosition: { string: number; fret: number } | null,
  maxFret = 15
): { string: number; fret: number; note: NoteName; midi: number } | null {
  return findBestPositionExcluding(targetMidi, prevPosition, new Set(), maxFret);
}

export function findBestPositionExcluding(
  targetMidi: number,
  prevPosition: { string: number; fret: number } | null,
  excludedStrings: Set<number>,
  maxFret = 15,
  preferLowStrings = false
): { string: number; fret: number; note: NoteName; midi: number } | null {
  const allPositions = getAllPositions(targetMidi, maxFret).filter(
    (p) => !excludedStrings.has(p.string)
  );
  if (allPositions.length === 0) return null;
  if (!prevPosition) {
    return allPositions.sort((a, b) => {
      const openBonusA = a.fret === 0 ? -20 : 0;
      const openBonusB = b.fret === 0 ? -20 : 0;
      const scoreA =
        a.fret * 2 + (preferLowStrings ? -a.string * 3 : Math.abs(a.string - 3.5)) + openBonusA;
      const scoreB =
        b.fret * 2 + (preferLowStrings ? -b.string * 3 : Math.abs(b.string - 3.5)) + openBonusB;
      return scoreA - scoreB;
    })[0];
  }
  return allPositions.sort((a, b) => {
    let distA = Math.abs(a.string - prevPosition.string) + Math.abs(a.fret - prevPosition.fret);
    let distB = Math.abs(b.string - prevPosition.string) + Math.abs(b.fret - prevPosition.fret);
    if (a.fret === 0) distA -= 3;
    if (b.fret === 0) distB -= 3;
    if (distA !== distB) return distA - distB;
    if (preferLowStrings) return b.string - a.string;
    return a.fret - b.fret;
  })[0];
}

function assignChordPositions(
  chordNotes: TimedMusicXmlNote[],
  prevPosByVoice: Map<number, { string: number; fret: number }>,
  bassMidiToString: Map<number, number>,
  preferredStringByVoiceMidi: Map<string, number>,
  maxFret = 15
): Map<number, { string: number; fret: number; note: NoteName; midi: number }> | null {
  if (chordNotes.length === 0) return null;

  const fixed = new Map<number, { string: number; fret: number; note: NoteName; midi: number }>();
  const needsSolver: { note: TimedMusicXmlNote; index: number }[] = [];

  chordNotes.forEach((note, index) => {
    const tabPos = note.isRest ? null : positionFromTablature(note);
    if (tabPos) {
      fixed.set(index, tabPos);
    } else if (!note.isRest) {
      needsSolver.push({ note, index });
    }
  });

  if (needsSolver.length === 0) {
    const strings = new Set<number>();
    for (const pos of fixed.values()) {
      if (strings.has(pos.string)) return null;
      strings.add(pos.string);
    }
    return fixed;
  }

  const usedStrings = new Set(Array.from(fixed.values(), (p) => p.string));
  const optionsPerNote = needsSolver.map(({ note }) =>
    getAllPositions(note.midi, maxFret).filter((p) => !usedStrings.has(p.string))
  );

  if (optionsPerNote.some((opts) => opts.length === 0)) return null;

  let combinations: { string: number; fret: number; note: NoteName; midi: number }[][] = [[]];
  for (const opts of optionsPerNote) {
    const next: { string: number; fret: number; note: NoteName; midi: number }[][] = [];
    for (const combo of combinations) {
      for (const opt of opts) {
        next.push([...combo, opt]);
      }
    }
    combinations = next;
  }

  const valid = combinations.filter((combo) => {
    const strings = new Set([...usedStrings, ...combo.map((p) => p.string)]);
    return strings.size === usedStrings.size + combo.length;
  });

  if (valid.length === 0) return null;

  const scored = valid.map((combo) => {
    let score = 0;

    // ── 1) Construir el acorde completo (fijos + solver) ordenado por MIDI ascendente ──
    const allEntries: { midi: number; pos: { string: number; fret: number }; isFixed: boolean }[] = [];
    for (const [idx, pos] of fixed.entries()) {
      const midi = chordNotes[idx]?.midi;
      if (midi !== undefined) allEntries.push({ midi, pos, isFixed: true });
    }
    needsSolver.forEach(({ note }, i) => {
      allEntries.push({ midi: note.midi, pos: combo[i], isFixed: false });
    });
    allEntries.sort((a, b) => a.midi - b.midi);

    // ── 2) Costes base: trastes y compacidad del acorde ──
    score += combo.reduce((s, p) => s + p.fret * 2, 0);

    const allFrets = allEntries.map((e) => e.pos.fret);
    const playedFrets = allFrets.filter((f) => f > 0);
    if (playedFrets.length > 0) {
      const span = Math.max(...playedFrets) - Math.min(...playedFrets);
      if (span > 4) score += (span - 4) * 30;
      if (span > 0) score += span * 2;
    }
    if (playedFrets.length > 0 && allFrets.some((f) => f === 0)) {
      const maxPlayed = Math.max(...playedFrets);
      if (maxPlayed > 4) score += (maxPlayed - 4) * 18;
    }

    // ── 3) Orden monótono: pitch ascendente → cuerda descendente (1 = aguda) ──
    for (let i = 0; i < allEntries.length - 1; i++) {
      const a = allEntries[i];
      const b = allEntries[i + 1];
      if (a.pos.string < b.pos.string) {
        score += 18;
      } else if (a.pos.string === b.pos.string) {
        score += 50;
      }
    }

    // ── 4) Bonus mástil-cerca y compacidad de cuerdas ──
    const usedStringsCombo = allEntries.map((e) => e.pos.string).sort((a, b) => a - b);
    const stringSpan = usedStringsCombo[usedStringsCombo.length - 1] - usedStringsCombo[0];
    if (stringSpan > allEntries.length - 1 + 1) {
      score += (stringSpan - allEntries.length) * 4;
    }

    // ── 5) Coste por nota (voz, preferencias, dedos) ──
    combo.forEach((pos, i) => {
      const note = needsSolver[i].note;
      const voice = note.voice ?? 1;
      const voicePrev = prevPosByVoice.get(voice);
      if (voicePrev) {
        score += Math.abs(pos.string - voicePrev.string) + Math.abs(pos.fret - voicePrev.fret) * 0.6;
      }

      const isMultiNoteChord = allEntries.length > 1;
      if (pos.fret === 0) {
        score -= isMultiNoteChord ? 3 : 12;
      }

      const prefKey = midiStringPreferenceKey(voice, note.midi);
      const lockedString = preferredStringByVoiceMidi.get(prefKey);
      if (lockedString !== undefined && pos.string !== lockedString) score += 6;

      if (voice >= 3) {
        if (pos.string <= 3) score += 30;
        const bassString = bassMidiToString.get(note.midi);
        if (bassString !== undefined && pos.string !== bassString) score += 12;
      } else {
        if (pos.string >= 5) score += 8;
      }

      if (note.leftHandFingering) {
        const finger = parseInt(note.leftHandFingering, 10);
        if (!Number.isNaN(finger) && Math.abs(pos.fret - (finger - 1)) <= 1) {
          score -= 4;
        }
      }
    });

    // ── 6) Misma nota física en distintas voces debe ir a la misma cuerda ──
    const midiToString = new Map<number, number>();
    for (const e of allEntries) {
      if (midiToString.has(e.midi) && midiToString.get(e.midi) !== e.pos.string) {
        score += 1000;
      }
      midiToString.set(e.midi, e.pos.string);
    }

    return { combo, score };
  });

  scored.sort((a, b) => a.score - b.score);
  const best = scored[0].combo;

  const result = new Map<number, { string: number; fret: number; note: NoteName; midi: number }>(fixed);
  needsSolver.forEach(({ index }, i) => {
    result.set(index, best[i]);
  });
  return result;
}

/** Agrupa por instante (todas las voces) — útil para visualización global. */
export function groupNotesByStartTime(notes: TimedMusicXmlNote[]): TimedMusicXmlNote[][] {
  const groups: TimedMusicXmlNote[][] = [];
  for (const note of notes) {
    const last = groups[groups.length - 1];
    if (last && last[0].startTime === note.startTime) {
      last.push(note);
    } else {
      groups.push([note]);
    }
  }
  return groups;
}

/** Agrupa por instante y voz — acordes de guitarra por voz (melodía / bajo separados). */
export function groupNotesByStartTimeAndVoice(notes: TimedMusicXmlNote[]): TimedMusicXmlNote[][] {
  const groups: TimedMusicXmlNote[][] = [];
  for (const note of notes) {
    const voice = note.voice ?? 1;
    const last = groups[groups.length - 1];
    if (last && last[0].startTime === note.startTime && (last[0].voice ?? 1) === voice) {
      last.push(note);
    } else {
      groups.push([note]);
    }
  }
  return groups;
}

function notePositionKey(note: TimedMusicXmlNote): string {
  return `${note.startTime}|${note.voice ?? 1}|${note.midi}|${note.duration}|${note.type}`;
}

function buildPositionedNote(
  note: TimedMusicXmlNote,
  pos: { string: number; fret: number }
): PositionedNote {
  return {
    ...note,
    string: pos.string,
    fret: pos.fret,
    x: 0,
    width: 0,
    color: STRING_COLORS[pos.string],
    colorDark: STRING_COLORS_DARK[pos.string],
    fretLabel: pos.fret === 0 ? "0" : String(pos.fret),
    typeLabel: getNoteTypeLabel(note.type),
  };
}

export function assignPositions(
  notes: TimedMusicXmlNote[],
  maxFret = 15
): PositionedNote[] {
  const sorted = [...notes].sort(
    (a, b) =>
      a.startTime - b.startTime ||
      (a.voice ?? 1) - (b.voice ?? 1) ||
      a.midi - b.midi
  );

  const positionByKey = new Map<string, PositionedNote>();
  const prevPosByVoice = new Map<number, { string: number; fret: number }>();
  const bassMidiToString = new Map<number, number>();
  const preferredStringByVoiceMidi = new Map<string, number>();

  const groups = groupNotesByStartTimeAndVoice(sorted);

  for (const group of groups) {
    const realNotes = group.filter((n) => !n.isRest);
    const rests = group.filter((n) => n.isRest);

    let chordAssignments: Map<number, { string: number; fret: number; note: NoteName; midi: number }> | null =
      null;

    if (realNotes.length > 0) {
      chordAssignments = assignChordPositions(
        realNotes,
        prevPosByVoice,
        bassMidiToString,
        preferredStringByVoiceMidi,
        maxFret
      );
    }

    const usedStrings = new Set<number>();
    const midiToPos = new Map<number, { string: number; fret: number }>();

    for (let i = 0; i < realNotes.length; i++) {
      const note = realNotes[i];
      const voice = note.voice ?? 1;
      let pos = chordAssignments?.get(i) ?? positionFromTablature(note);

      if (!pos) {
        const cached = midiToPos.get(note.midi);
        if (cached) {
          pos = { ...cached, note: note.step as NoteName, midi: note.midi };
        } else {
          const isBass = voice >= 3;
          const voicePrev = prevPosByVoice.get(voice);
          const prefKey = midiStringPreferenceKey(voice, note.midi);
          const preferredString = preferredStringByVoiceMidi.get(prefKey);

          let individual = findBestPositionExcluding(
            note.midi,
            voicePrev ?? null,
            usedStrings,
            maxFret,
            isBass
          );

          if (preferredString !== undefined && individual && preferredString !== individual.string) {
            const sameStringOption = getAllPositions(note.midi, maxFret).find(
              (p) => p.string === preferredString && !usedStrings.has(p.string)
            );
            if (sameStringOption) individual = sameStringOption;
          }

          if (!individual) {
            individual = findBestPositionExcluding(note.midi, voicePrev ?? null, new Set(), maxFret, isBass);
          }

          if (individual) pos = individual;
        }
      }

      if (pos) {
        positionByKey.set(notePositionKey(note), buildPositionedNote(note, pos));
        usedStrings.add(pos.string);
        midiToPos.set(note.midi, { string: pos.string, fret: pos.fret });
        prevPosByVoice.set(voice, { string: pos.string, fret: pos.fret });

        const prefKey = midiStringPreferenceKey(voice, note.midi);
        if (!preferredStringByVoiceMidi.has(prefKey)) {
          preferredStringByVoiceMidi.set(prefKey, pos.string);
        }
        if (voice >= 3) {
          bassMidiToString.set(note.midi, pos.string);
        }
      }
    }

    for (const note of rests) {
      positionByKey.set(notePositionKey(note), {
        ...note,
        string: 1,
        fret: 0,
        x: 0,
        width: 0,
        color: STRING_COLORS[1],
        colorDark: STRING_COLORS_DARK[1],
        fretLabel: "—",
        typeLabel: getNoteTypeLabel(note.type),
      });
    }
  }

  for (const note of sorted) {
    if (positionByKey.has(notePositionKey(note))) continue;
    if (note.isRest) {
      positionByKey.set(notePositionKey(note), {
        ...note,
        string: 1,
        fret: 0,
        x: 0,
        width: 0,
        color: STRING_COLORS[1],
        colorDark: STRING_COLORS_DARK[1],
        fretLabel: "—",
        typeLabel: getNoteTypeLabel(note.type),
      });
      continue;
    }
    const fallback = findBestPositionExcluding(
      note.midi,
      prevPosByVoice.get(note.voice ?? 1) ?? null,
      new Set(),
      maxFret,
      (note.voice ?? 1) >= 3
    );
    if (fallback) {
      positionByKey.set(notePositionKey(note), buildPositionedNote(note, fallback));
    }
  }

  return sorted
    .map((n) => positionByKey.get(notePositionKey(n)))
    .filter((p): p is PositionedNote => p !== undefined);
}

// ============================================
// Utilidades visuales
// ============================================

export function getNoteTypeLabel(type: string): string {
  const map: Record<string, string> = {
    whole: "redonda",
    half: "blanca",
    quarter: "negra",
    eighth: "corchea",
    "16th": "semicorchea",
    "32nd": "fusa",
    "64th": "semifusa",
  };
  return map[type] || type;
}

export function getNoteWidth(duration: number, divisions: number, pixelsPerBeat = 80): number {
  const beats = duration / divisions;
  return beats * pixelsPerBeat;
}

/**
 * Layout horizontal por compás.
 * **Anacrusa**: ancho visual proporcional a `contentLength` (solo lo escrito en el XML).
 */
export interface HighwayTimeLayout {
  measureInfos: MeasureInfo[];
  divisions: number;
  pixelsPerBeat: number;
  /** Left edge X (px) de cada compás en coords de contenido (sin MARGEN mastil). */
  measureStartXs: number[];
  /** Ancho visual (px) de cada compás. */
  measureVisualWidths: number[];
  totalContentWidth: number;
  /** X de cualquier instante global en divisiones (incluye colas de silencio dentro del compás). */
  contentXAtDivision: (globalDiv: number) => number;
  blockWidthAtStartTime: (
    startTime: number,
    allNotes: Array<{ startTime: number; duration: number; isRest?: boolean }>
  ) => number;
}

function findMeasureIndexForDivision(t: number, infos: MeasureInfo[]): number {
  if (infos.length === 0) return 0;
  for (let i = 0; i < infos.length; i++) {
    const m = infos[i];
    if (t + 1e-9 >= m.startTime && t < m.startTime + m.length - 1e-9) return i;
  }
  if (t < infos[0].startTime) return 0;
  return infos.length - 1;
}

export function buildHighwayTimeLayout(
  measureInfos: MeasureInfo[],
  divisions: number,
  pixelsPerBeat: number
): HighwayTimeLayout | null {
  if (!measureInfos.length || divisions <= 0) return null;

  measureInfos = [...measureInfos].sort((a, b) => a.startTime - b.startTime);

  const measureStartXs: number[] = [];
  const measureVisualWidths: number[] = [];
  let cum = 0;

  for (const m of measureInfos) {
    measureStartXs.push(cum);
    const spanVisual = m.isPickup ? m.contentLength : m.length;
    const w = Math.max((spanVisual / divisions) * pixelsPerBeat, m.isPickup ? 32 : 48);
    measureVisualWidths.push(w);
    cum += w;
  }

  const totalContentWidth = cum;

  const measureSpanDiv = (m: MeasureInfo) => (m.isPickup ? m.contentLength : m.length);

  const contentXAtDivision = (globalDiv: number): number => {
    const last = measureInfos[measureInfos.length - 1];
    const pieceEnd = last.startTime + last.length;
    if (globalDiv + 1e-9 >= pieceEnd) {
      return totalContentWidth;
    }

    const mi = findMeasureIndexForDivision(globalDiv, measureInfos);
    const m = measureInfos[mi];
    const base = measureStartXs[mi];
    const w = measureVisualWidths[mi];
    const L = Math.max(measureSpanDiv(m), 1e-9);
    const rel = Math.max(0, Math.min(globalDiv - m.startTime, L));
    return base + (rel / L) * w;
  };

  const blockWidthAtStartTime = (
    startTime: number,
    allNotes: Array<{ startTime: number; duration: number; isRest?: boolean }>
  ): number => {
    const mi = findMeasureIndexForDivision(startTime, measureInfos);
    const m = measureInfos[mi];
    const w = measureVisualWidths[mi];
    const L = Math.max(measureSpanDiv(m), 1e-9);
    const atTime = allNotes.filter((n) => n.startTime === startTime && !n.isRest);
    const d =
      atTime.length > 0 ? Math.max(...atTime.map((n) => Math.max(1, n.duration))) : L / 4;
    return Math.max((d / L) * w, 22);
  };

  return {
    measureInfos,
    divisions,
    pixelsPerBeat,
    measureStartXs,
    measureVisualWidths,
    totalContentWidth,
    contentXAtDivision,
    blockWidthAtStartTime,
  };
}

// ─── Mástil: escala visual relativa (legibilidad sin romper alineación temporal) ───

export interface MastilRelativeWidthOptions {
  /** Ancho de la figura más corta (referencia). */
  minPx: number;
  /** Máximo ancho para figuras largas (evita desbordar). */
  maxPx: number;
  /** 1 = lineal; &lt;1 comprime duraciones largas (jerarquía sin desbordar). */
  gamma: number;
  /** Separación mínima entre pastillas al recortar al hueco del pentagrama. */
  gapPx: number;
}

const DEFAULT_MASTIL_RELATIVE: MastilRelativeWidthOptions = {
  minPx: 34,
  maxPx: 108,
  gamma: 0.88,
  gapPx: 5,
};

/**
 * Anchos de pastilla por ataque: el intervalo más corto entre ataques recibe `minPx`;
 * el resto crece como (span/minSpan)^gamma, acotado por `maxPx`.
 */
export function computeMastilRelativeBlockWidths(
  notes: Array<{ startTime: number; duration: number; isRest?: boolean }>,
  sortedAttackTimes: number[],
  measureInfos: MeasureInfo[],
  options?: Partial<MastilRelativeWidthOptions>
): Map<number, number> {
  const { minPx, maxPx, gamma } = { ...DEFAULT_MASTIL_RELATIVE, ...options };
  const times = [...sortedAttackTimes].filter((t) => Number.isFinite(t)).sort((a, b) => a - b);
  if (times.length === 0) return new Map();

  const endPiece =
    measureInfos.length > 0
      ? measureInfos[measureInfos.length - 1].startTime + measureInfos[measureInfos.length - 1].length
      : times[times.length - 1] +
        (times.length >= 2 ? Math.max(1, times[times.length - 1] - times[times.length - 2]) : 1);

  const spanEndFor = (i: number): number => {
    const start = times[i];
    const nextAttack = i + 1 < times.length ? times[i + 1] : endPiece;
    const atT = notes.filter((n) => n.startTime === start && !n.isRest);
    const durEnd = atT.length ? start + Math.max(1e-6, ...atT.map((n) => n.duration)) : nextAttack;
    return Math.min(nextAttack, durEnd, endPiece);
  };

  const spans: number[] = [];
  for (let i = 0; i < times.length; i++) {
    const start = times[i];
    const endT = spanEndFor(i);
    spans.push(Math.max(1, endT - start));
  }

  const minSpan = Math.min(...spans);
  const map = new Map<number, number>();
  for (let i = 0; i < times.length; i++) {
    const t = times[i];
    const ratio = spans[i] / minSpan;
    const raw = minPx * Math.pow(ratio, gamma);
    map.set(t, Math.round(Math.min(maxPx, Math.max(minPx * 0.9, raw))));
  }
  return map;
}

export interface MastilBlockLayoutEntry {
  x: number;
  width: number;
}

/**
 * Coloca cada pastilla al inicio del hueco [mastilX(t), mastilX(tNext)) (alineada al ataque como el pentagrama).
 * El ancho sigue siendo el relativo, recortado al hueco para no solaparse con el siguiente ataque.
 */
export function layoutMastilBlocksInStaffSlots(
  sortedAttackTimes: number[],
  relativeWidths: Map<number, number>,
  mastilXAt: (divisionTime: number) => number,
  measureInfos: MeasureInfo[],
  gapPx: number,
  /** Si se pasan, el hueco derecho de cada ataque respeta la duración real (evita pastillas “eternas” hasta el final de la pieza). */
  notes?: Array<{ startTime: number; duration: number; isRest?: boolean }>
): Map<number, MastilBlockLayoutEntry> {
  const gap = gapPx;
  const times = [...sortedAttackTimes].filter((t) => Number.isFinite(t)).sort((a, b) => a - b);
  const out = new Map<number, MastilBlockLayoutEntry>();
  if (times.length === 0) return out;

  const endPiece =
    measureInfos.length > 0
      ? measureInfos[measureInfos.length - 1].startTime + measureInfos[measureInfos.length - 1].length
      : times[times.length - 1] +
        (times.length >= 2 ? Math.max(1, times[times.length - 1] - times[times.length - 2]) : 1);

  for (let i = 0; i < times.length; i++) {
    const t = times[i];
    const xSlotStart = mastilXAt(t);
    let endDiv: number;
    if (i + 1 < times.length) {
      endDiv = times[i + 1];
    } else if (notes && notes.length > 0) {
      const atT = notes.filter((n) => n.startTime === t && !n.isRest);
      const dur = atT.length ? Math.max(1e-6, ...atT.map((n) => n.duration)) : endPiece - t;
      endDiv = Math.min(endPiece, t + dur);
    } else {
      endDiv = endPiece;
    }
    if (endDiv <= t + 1e-6) {
      endDiv = Math.min(endPiece, t + 1);
    }
    const xSlotEnd = mastilXAt(endDiv);
    const slotW = Math.max(gap * 2 + 4, xSlotEnd - xSlotStart);
    const wRaw = relativeWidths.get(t) ?? DEFAULT_MASTIL_RELATIVE.minPx;
    const wFit = Math.min(wRaw, slotW - gap);
    const w = Math.max(22, wFit);
    const x = xSlotStart;
    out.set(t, { x, width: w });
  }
  return out;
}

export function getNoteX(startTime: number, divisions: number, pixelsPerBeat = 80): number {
  const beats = startTime / divisions;
  return beats * pixelsPerBeat;
}

export function computeOptimalPixelsPerBeat(
  notes: TimedMusicXmlNote[],
  divisions: number,
  minNoteWidth = 36,
  maxPixelsPerBeat = 160
): number {
  const realNotes = notes.filter((n) => !n.isRest && n.duration > 0);
  if (realNotes.length === 0) return 80;
  const minDuration = Math.min(...realNotes.map((n) => n.duration));
  const optimal = (minNoteWidth * divisions) / minDuration;
  return Math.min(Math.max(optimal, 48), maxPixelsPerBeat);
}

export function getDurationSeconds(duration: number, divisions: number, tempo: number): number {
  const beats = duration / divisions;
  return (beats * 60) / tempo;
}

export function getStartSeconds(startTime: number, divisions: number, tempo: number): number {
  const beats = startTime / divisions;
  return (beats * 60) / tempo;
}

// ============================================
// Preparación completa para renderizado
// ============================================

export interface PreparedSong {
  notes: PositionedNote[];
  divisions: number;
  tempo: number;
  timeSignature: { beats: number; beatType: number };
  totalDivisions: number;
  totalWidth: number;
  pixelsPerBeat: number;
  clefOctaveChange: number;
  xmlClefOctaveChange: number;
  measures: MeasureInfo[];
}

export function prepareSongForRendering(
  notes: TimedMusicXmlNote[],
  meta: {
    divisions: number;
    tempo: number;
    timeSignature: { beats: number; beatType: number };
    totalDivisions: number;
    clefOctaveChange?: number;
    xmlClefOctaveChange?: number;
    measures?: MeasureInfo[];
  },
  options: { maxFret?: number; pixelsPerBeat?: number; minNoteWidth?: number } = {}
): PreparedSong {
  const { maxFret = 15, pixelsPerBeat: userPpb, minNoteWidth = 36 } = options;
  const pixelsPerBeat = userPpb ?? computeOptimalPixelsPerBeat(notes, meta.divisions, minNoteWidth);

  const sorted = [...notes].sort(
    (a, b) =>
      a.startTime - b.startTime ||
      (a.voice ?? 1) - (b.voice ?? 1) ||
      a.midi - b.midi
  );
  const positioned = assignPositions(sorted, maxFret);

  const layout =
    meta.measures && meta.measures.length > 0
      ? buildHighwayTimeLayout(meta.measures, meta.divisions, pixelsPerBeat)
      : null;

  const renderNotes = positioned.map((note) => ({
    ...note,
    width: layout
      ? layout.blockWidthAtStartTime(note.startTime, positioned)
      : getNoteWidth(note.duration, meta.divisions, pixelsPerBeat),
    x: layout
      ? layout.contentXAtDivision(note.startTime)
      : getNoteX(note.startTime, meta.divisions, pixelsPerBeat),
  }));

  const maxRight = renderNotes.reduce((mx, n) => Math.max(mx, n.x + n.width), 0);
  const totalWidth = Math.max(
    layout ? layout.totalContentWidth : getNoteX(meta.totalDivisions, meta.divisions, pixelsPerBeat),
    maxRight
  );

  return {
    notes: renderNotes,
    divisions: meta.divisions,
    tempo: meta.tempo,
    timeSignature: meta.timeSignature,
    totalDivisions: meta.totalDivisions,
    totalWidth,
    pixelsPerBeat,
    clefOctaveChange: meta.clefOctaveChange ?? GUITAR_DISPLAY_CLEF_OCTAVE_CHANGE,
    xmlClefOctaveChange: meta.xmlClefOctaveChange ?? 0,
    measures: meta.measures ?? [],
  };
}
